import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { X, PaperPlaneTilt } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import ApproverSelect from '@/components/approvals/ApproverSelect'
import { useScrollLock } from '@/lib/useScrollLock'

const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }
const labelClass = 'block mb-1.5 text-muted-foreground dark:text-[#8a8f98]'

interface SubmitForApprovalDialogProps {
  isOpen: boolean
  onClose: () => void
  workflowName: string
  /** Whoever the previous request went to; defaulted to when still eligible, so a resubmission follows the named approver. */
  preferredApproverId?: number | null
  /** Resolves once the workflow has been submitted; the dialog closes itself afterwards. */
  onSubmit: (approverId: number) => Promise<void>
}

/**
 * "Send for approval": a Representative picks which Owner/Admin/Manager
 * reviews their workflow before it goes live (spec 5.2). The approver
 * picker reports the account default as soon as it loads, so the primary
 * button is usable without touching the control. Errors are surfaced by the
 * caller (the store toasts), so this dialog only tracks the pending state.
 */
export const SubmitForApprovalDialog: React.FC<SubmitForApprovalDialogProps> = ({
  isOpen,
  onClose,
  workflowName,
  preferredApproverId,
  onSubmit,
}) => {
  const [approverId, setApproverId] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  useScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleSubmit = async () => {
    if (approverId == null || isSubmitting) return
    setIsSubmitting(true)
    try {
      await onSubmit(approverId)
      onClose()
    } catch {
      /* the caller (workflowStore) already toasts the failure */
    } finally {
      setIsSubmitting(false)
    }
  }

  const name = workflowName.trim() || 'This workflow'

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label="Send for approval"
    >
      <motion.div
        className="fixed top-0 left-0 w-screen h-screen"
        onClick={onClose}
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
      />
      <motion.div
        className="relative z-10 bg-card border border-border/60 rounded-2xl shadow-2xl dark:bg-[#0f1011] dark:border-white/[0.08] p-6 w-full max-w-md"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        initial={{ opacity: 0, y: 8, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="flex items-center justify-between mb-5">
          <h2
            className="text-foreground dark:text-[#f7f8f8] flex items-center gap-2"
            style={{ fontSize: '18px', fontWeight: 590, lineHeight: 1.33, letterSpacing: '-0.24px' }}
          >
            <PaperPlaneTilt className="h-5 w-5 text-[#5e6ad2]" weight="fill" />
            Send for approval
          </h2>
          <button
            onClick={onClose}
            aria-label="Close send-for-approval dialog"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150"
          >
            <X className="h-4 w-4" weight="regular" />
          </button>
        </div>

        <div className="space-y-4">
          <p className="text-[13px] leading-relaxed text-muted-foreground dark:text-[#8a8f98]">
            <span className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 510 }}>{name}</span>
            {' '}will be sent to the approver you choose. They can approve it or send it back with a reason.
          </p>
          <div>
            <label htmlFor="submit-approval-approver" className={labelClass} style={labelStyle}>
              Send to
            </label>
            <ApproverSelect
              id="submit-approval-approver"
              value={approverId}
              onChange={setApproverId}
              preferredId={preferredApproverId}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            loading={isSubmitting}
            disabled={approverId == null}
            onClick={handleSubmit}
            icon={<PaperPlaneTilt className="w-4 h-4" />}
          >
            Send for approval
          </Button>
        </div>
      </motion.div>
    </div>,
    document.body,
  )
}

export default SubmitForApprovalDialog
